import {
  type Dictionary,
  type Segment,
  segment,
  type Syllable,
  writeSyllable,
} from "@kensio/pinyinjs";

import type { CidianIndex } from "./cidian-index.js";
import type { HskBand } from "../content/hsk/hsk-band.js";

/**
 * Turning a text into the list of words it is made of.
 *
 * The tool's whole job, and the part with no DOM in it. `segment` hands back
 * every stretch of the text in order, including the stretches that were never
 * Han. What is left to do is decide which of those are words and gather the
 * repeats. Putting the gathered list in an order a learner can use is
 * `word-order.ts`, which is a question about a list where this is a question
 * about a text.
 */

/** One distinct word of a text, with everything the list shows about it. */
export interface SegmentedWord {
  /** The characters, in whichever script the text wrote them. */
  readonly text: string;

  /** The 普通话 reading the decoder settled on for this word. */
  readonly reading: readonly Syllable[];

  /** How many times the text uses it. */
  readonly count: number;

  /** Where it first appears, in code points from the start of the text. */
  readonly firstAt: number;

  /**
   * How common the word is in general, 0 to 15, or nothing where the
   * dictionary has no entry for it.
   */
  readonly frequency: number | undefined;

  /**
   * The band the word's 词典 entry carries, where it has one and is graded.
   *
   * How hard a word is rather than how common, which is the other band on the
   * row. 银行 is a common word and an HSK 3 one, and 的 is commoner still and
   * HSK 1.
   */
  readonly band: HskBand | undefined;

  /**
   * Where the word's 词典 entry is, or nothing where the site has no page.
   *
   * Read here rather than at the row, so that a word carries everything the
   * list shows about it and the drawing has nothing left to look up.
   */
  readonly address: string | undefined;

  /** The canonical headword saved for this writing. */
  readonly headword: string | undefined;

  /** jieba's tag for the first occurrence, or the empty string. */
  readonly partOfSpeech: string;

  readonly isProperNoun: boolean;
}

/**
 * Whether a segment is a word this list should hold.
 *
 * `Segment.isKnown` is false for a stretch that was never Han, which covers
 * the spaces, the punctuation, the Latin and the digits. It is also false for
 * a Han character no source has a reading for, and those stay out too: a row
 * offering no reading and no frequency has nothing in it but the character.
 *
 * A word arriving with no reading is treated the same way for the same reason,
 * although `segment` gives every known Han word one.
 */
function isWord(found: Segment): boolean {
  return found.isKnown && found.reading.length > 0;
}

/**
 * What separates one row from another.
 *
 * **The writing and the reading together, and not the writing alone.** 得 is
 * `de` after a verb and `děi` before one, and a list collapsing those into one
 * row has published a word with two pronunciations and picked one. The 词典
 * nests senses inside readings for exactly this reason, and a word list that
 * disagreed with it would be the one page on the site that did.
 *
 * The reading is written out rather than compared syllable by syllable,
 * because a key has to be a string and `writeSyllable` is what the rest of the
 * site writes a syllable with.
 */
function keyOf(found: Segment): string {
  return `${found.text} ${found.reading.map((syllable) => writeSyllable(syllable)).join(" ")}`;
}

/**
 * Every distinct word of a text, in the order it first appears.
 *
 * Ordering is `orderedWords`'s. This one gathers, so that a caller changing
 * the order never re-segments and the sort is over a few hundred objects
 * rather than a few thousand.
 *
 * The frequency of each word is looked up once here, for the same reason, and
 * so are the band and the address its 词典 entry gives it.
 */
export function segmentedWords(
  dictionary: Dictionary,
  text: string,
  cidian: CidianIndex,
): readonly SegmentedWord[] {
  const gathered = new Map<string, SegmentedWord>();

  for (const found of segment(dictionary, text)) {
    if (!isWord(found)) {
      continue;
    }

    const key = keyOf(found);
    const held = gathered.get(key);

    if (held === undefined) {
      gathered.set(key, {
        text: found.text,
        reading: found.reading,
        count: 1,
        firstAt: found.at,
        frequency: dictionary.frequencyOf(found.text),
        band: cidian.bandOf(found.text),
        address: cidian.addressOf(found.text),
        headword: cidian.headwordOf(found.text),
        partOfSpeech: found.partOfSpeech,
        isProperNoun: found.isProperNoun,
      });
    } else {
      gathered.set(key, { ...held, count: held.count + 1 });
    }
  }

  return [...gathered.values()];
}

/** How many words the text holds in total, counting every repeat. */
export function totalWords(words: readonly SegmentedWord[]): number {
  return words.reduce((running, word) => running + word.count, 0);
}
